import { modalApi } from '@linepay/react-components';
import {
  cloneElement,
  MouseEvent,
  ReactElement,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
export { LayerPortal } from '@linepay/react-components';

export class ModalError<T = unknown> extends Error {
  payload?: T;

  constructor(message: string, payload?: T) {
    super(message);
    this.payload = payload;
  }
}

export type ModalOptions = Partial<Parameters<(typeof modalApi)['create']>[0]> & {
  throwOnDismiss?: boolean;
};

export class ModalCustomEvent<T> extends Event {
  data: T;

  constructor(data: T) {
    super('modalcustom');
    this.data = data;
  }
}

export type ConfirmEvent<T> = {
  confirmed: boolean;
} & T;

export type ModalComponentProps<T = MouseEvent> = {
  onConfirm?: (event?: T) => void;
  onDismiss?: (event?: T) => void;
};

export class ModalAdapter<T = MouseEvent> {
  resolve?: (value: any) => void;
  reject?: (arg?: any) => void;
  modalId?: string | number;
  modalOptions: ModalOptions;

  constructor(options?: ModalOptions) {
    this.modalOptions = {
      onBackdropClick: true,
      closeOnBack: false,
      throwOnDismiss: false,
      ...options,
    };
  }

  open(content: ReactElement): Promise<ConfirmEvent<T>> {
    return new Promise<ConfirmEvent<T>>((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
      const confirmEventHandler = (confirmed: boolean, event?: T) => {
        const confirmEvent = {
          confirmed,
          ...event,
        } as ConfirmEvent<T>;

        if (this.modalOptions.throwOnDismiss && !confirmed) {
          reject(new ModalError('DISMISSED', event));
        } else {
          resolve(confirmEvent);
        }
        modalApi.remove(this.modalId);
      };

      const onConfirm = (event?: T) => confirmEventHandler(true, event);
      const onDismiss = (event?: T) => confirmEventHandler(false, event);
      const onBackdropClick = () => {
        if (this.modalOptions.onBackdropClick === true) {
          onDismiss();
        } else if (typeof this.modalOptions.onBackdropClick === 'function') {
          this.modalOptions.onBackdropClick(() => this.close());
        }
      };

      this.modalId = modalApi.create({
        content: cloneElement(content, { onConfirm, onDismiss }),
        ...this.modalOptions,
        onBackdropClick,
      });
    });
  }

  confirm(data?: any) {
    this.resolve?.({ confirmed: true, ...data });
    return modalApi.remove(this.modalId);
  }

  close(data?: any) {
    this.resolve?.({ confirmed: false, ...data });
    return modalApi.remove(this.modalId);
  }

  cancel() {
    this.reject?.({ cancelled: true });
    return modalApi.remove(this.modalId);
  }
}

export const useModal = (defaultOptions?: ModalOptions) => {
  const stack = useRef<ModalAdapter<any>[]>([]);

  const remove = useCallback((id?: ModalOptions['id']) => {
    const index = stack.current.findIndex((modal) => modal.modalId === id);
    if (index > -1) {
      const modal = stack.current[index];
      stack.current.splice(index, 1);
      return modal;
    }
  }, []);

  const confirm = useCallback(
    (id?: ModalOptions['id'], data?: any) => {
      if (id === undefined) {
        stack.current.forEach((modal) => modal.confirm(data));
        stack.current = [];
      } else {
        remove(id)?.confirm(data);
      }
    },
    [remove]
  );

  const close = useCallback(
    (id?: ModalOptions['id'], data?: any) => {
      if (id === undefined) {
        stack.current.forEach((modal) => modal.close(data));
        stack.current = [];
      } else {
        remove(id)?.close(data);
      }
    },
    [remove]
  );

  const cancel = useCallback(() => {
    stack.current.forEach((modal) => modal.cancel());
    stack.current = [];
  }, []);

  const open = useCallback(
    <T = unknown>(content: ReactElement, options?: ModalOptions) => {
      const modal = new ModalAdapter<T>({ ...defaultOptions, ...options });
      stack.current.push(modal);
      return modal.open(content).finally(() => remove(modal.modalId));
    },
    [defaultOptions, remove]
  );

  useLayoutEffect(
    () => () => {
      window.requestAnimationFrame(() => cancel());
    },
    [cancel]
  );
  return { open, confirm, close, cancel };
};
